import { formatPacingNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { COMPANY_DIMENSION, GROSS_LABEL } from './linear-pacing-constants';

export const uniqueLines = <T extends object>(rows: T[], key: keyof T & string): object[] =>
  [...new Set(rows.map(row => row[key]))].map(value => ({ [key]: value }));

// "Companies (12)" / "Networks (40)"
export const paneTitle = (dimension: string, count: number): string =>
  `${dimension === COMPANY_DIMENSION ? 'Companies' : 'Networks'} (${count})`;

export interface ClearanceRow {
  week: string;
  clearance_rate: number | null;
  rate?: number;
  count_actual: number;
  count_expected: number;
  spend_actual: number;
  spend_expected: number;
  [groupingKey: string]: string | number | null | undefined;
}

export interface SeriesData {
  keyOrder: string[];
  groupedByKey: Record<string, ClearanceRow[]>;
  spendMap: Record<string, string>;
  clearanceMap: Record<string, string>;
}

const sumBy = (rows: ClearanceRow[], key: 'spend_actual' | 'spend_expected'): number =>
  rows.reduce((total, row) => total + row[key], 0);

// Pads each group out to one row per week in `range` (weeks with no data get a null clearance
// rate and no forecast rate, so both lines break) and converts rates to whole percentages.
const fillWeeks = (group: ClearanceRow[], range: string[]): ClearanceRow[] => {
  const rows = group.map(item => ({
    ...item,
    clearance_rate: Math.floor(Number(item.clearance_rate) * 100),
    rate: Math.floor(Number(item.rate) * 100)
  }));
  const filled: ClearanceRow[] = [];
  let rangeIdx = 0;
  let groupIdx = 0;
  while (groupIdx < rows.length || rangeIdx < range.length) {
    const groupHead = rows[groupIdx];
    if (rangeIdx >= range.length) {
      filled.push(groupHead);
      groupIdx++;
    } else if (groupIdx < rows.length && range[rangeIdx] === groupHead.week) {
      filled.push(groupHead);
      groupIdx++;
    } else {
      filled.push({
        ...groupHead,
        week: range[rangeIdx],
        clearance_rate: null,
        rate: undefined,
        count_actual: 0,
        count_expected: 0,
        spend_actual: 0,
        spend_expected: 0
      });
    }
    rangeIdx++;
  }
  return filled;
};

export const createSeriesData = (
  groupingKey: string,
  rows: ClearanceRow[],
  range: string[]
): SeriesData => {
  const grouped: Record<string, ClearanceRow[]> = {};
  rows.forEach(row => {
    const key = String(row[groupingKey]);
    (grouped[key] ??= []).push(row);
  });

  // Keys sorted by gross booked, largest first.
  const keyOrder = Object.keys(grouped)
    .map(key => ({ key, sum: sumBy(grouped[key], 'spend_expected') }))
    .sort((a, b) => b.sum - a.sum)
    .map(({ key }) => key);

  const groupedByKey: Record<string, ClearanceRow[]> = {};
  const spendMap: Record<string, string> = {};
  const clearanceMap: Record<string, string> = {};
  Object.entries(grouped).forEach(([key, group]) => {
    const filled = range.length === 0 ? group : fillWeeks(group, range);
    groupedByKey[key] = filled;
    spendMap[key] = formatPacingNumber(sumBy(filled, 'spend_expected'), '$');
    const clearance = (100 * sumBy(filled, 'spend_actual')) / sumBy(filled, 'spend_expected');
    clearanceMap[key] = Number.isFinite(clearance) ? `${clearance.toFixed(0)}%` : '—';
  });

  return { keyOrder, groupedByKey, spendMap, clearanceMap };
};

// Net spend. A null/undefined commission falls back to `fallbackCommission`: 8% on By Campaign,
// while Client Detail passes 0 so a null commission nets at 0.85. A real 0% commission stays 0%.
export const calculateAdjustedSpend = (
  spend: number,
  commission: number | null | undefined,
  spendDimension: string,
  fallbackCommission = 0.08
): number =>
  spendDimension === GROSS_LABEL ? spend : spend * (0.85 + (commission ?? fallbackCommission));

// A By Campaign 'Total' cell: the unsaved edit, else the saved override, else the logged spot
// cost. An override of 0 shows $0.
export const displayedSpend = (
  pendingOverride: number | undefined,
  savedOverride: number | null,
  spotCost: number
): number => pendingOverride ?? savedOverride ?? spotCost;

// Whether a By Campaign row has a saved override, including one of $0.
export const hasSpendOverride = (savedOverride: number | null | undefined): boolean =>
  savedOverride != null;
